import { Customer, Examination, Lead, Project, Report, SalesQuote, Status } from "graphql/types"
import { allExaminations } from "utils/allExaminations"

// Helper functions shared between desktop and mobile components

/**
 * Checks if a user is the project manager for a given project
 */
export const isUserProjectManager = (project: Project, userId: string): boolean => {
  return project?.management?.id === userId
}

/**
 * Checks if a user has assigned examinations in a project
 */
export const hasUserAssignedExaminations = (project: Project, userId: string): boolean => {
  if (!project?.devices) return false

  return allExaminations(project).some((examination: Examination) =>
    examination?.tasks?.some((task) => task?.examinator?.id === userId)
  )
}

/**
 * Validates if a project has the minimum required structure
 */
export const isValidProject = (project: Project): boolean => {
  return Boolean(project?.id)
}

/**
 * Filters projects based on user permissions and validation
 */
export const filterUserProjects = (projects: Project[], userId: string): Project[] => {
  return projects.filter((project) => {
    if (!isValidProject(project)) {
      return false
    }

    return (
      isUserProjectManager(project, userId) ||
      hasUserAssignedExaminations(project, userId)
    )
  })
}

/**
 * Filters examinations by status and user permissions
 */
export const filterExaminationsByStatusAndUser = (
  examinations: Examination[],
  statuses: Status[],
  userId: string,
  isProjectManager: boolean
): Examination[] => {
  if (!examinations?.length) return []

  // If no status filter, return all examinations
  if (statuses.length === 0) {
    return examinations
  }

  // Filter by status first
  const statusFiltered = examinations.filter(
    (examination) => examination?.status && statuses.includes(examination.status)
  )

  // For debugging: show all examinations regardless of user assignment
  // TODO: Remove this temporary fix once user assignment is properly set up
  return statusFiltered

  // Project managers see all examinations
  // if (isProjectManager) {
  //   return statusFiltered
  // }

  // Regular users only see their assigned examinations
  // return statusFiltered.filter((examination) =>
  //   examination?.tasks?.some((task) => task?.examinator?.id === userId)
  // )
}

/**
 * Sorts examinations by priority (current work first)
 */
export const sortExaminationsByPriority = (examinations: Examination[]): Examination[] => {
  return examinations.sort((a, b) => {
    const currentStatuses = [Status.OPEN, Status.IN_PROGRESS]
    const aIsCurrent = currentStatuses.includes(a.status)
    const bIsCurrent = currentStatuses.includes(b.status)

    if (aIsCurrent && !bIsCurrent) return -1
    if (!aIsCurrent && bIsCurrent) return 1
    return 0
  })
}

/**
 * Filters examinations based on search text
 */
export const filterExaminationsBySearch = (
  examinations: Examination[],
  searchText: string
): Examination[] => {
  if (!searchText.trim()) return examinations
  
  const searchLower = searchText.toLowerCase()
  return examinations.filter((exam) => 
    exam.name.toLowerCase().includes(searchLower) ||
    exam.norm.name.toLowerCase().includes(searchLower) ||
    exam.norm.label.toLowerCase().includes(searchLower)
  )
}

/**
 * Gets business customer name from opportunity ID
 */
export const getBusinessCustomerName = (
  opportunityID: string,
  leads: Lead[],
  customers: Customer[]
): string => {
  const lead = leads.find((lead) => lead.opportunityID === opportunityID)
  if (lead && lead.businessAccount) {
    const customer = customers.find((customer) => customer.customerID === lead.businessAccount)
    if (customer && customer.customerName) {
      return customer.customerName
    }
  }
  return ""
}

/**
 * Gets customer by ID
 */
export const getCustomerById = (id: string | undefined, customers: Customer[]): Customer | undefined => {
  return customers.find((c) => c.customerID === id)
}

/**
 * Finds primary sales quote for a project
 */
export const findPrimarySalesQuote = (quotes: SalesQuote[]): SalesQuote | undefined => {
  return quotes.find((quote) => quote.primary)
}

/**
 * Gets reports for a specific examination
 */
export const getReportsForExamination = (examinationId: string, projectReports: Report[]): Report[] => {
  return projectReports.filter((report) =>
    report.examinations.some((exam) => exam.id === examinationId)
  )
}

/**
 * Gets the single report for an examination (returns first if multiple exist)
 */
export const getReportForExamination = (examinationId: string, projectReports: Report[]): Report | null => {
  const reports = getReportsForExamination(examinationId, projectReports)
  return reports.length > 0 ? reports[0] : null
}
